// AccountStatusNotice.tsx - Estado de la cuenta de la empresa (prueba / bloqueo)
// - Durante la prueba gratis: franja con los días que quedan y cómo activar el plan.
// - Prueba vencida, cuenta suspendida o cancelada: pantalla con la explicación.
//   Antes el sistema simplemente dejaba de funcionar sin decir por qué.
import React, { useEffect, useState } from 'react';
import { Clock, Lock } from 'lucide-react';
import { useTenant } from '../contexts/TenantContext';
import { useAuth } from '../shared/hooks/useAuth';
import { ACCOUNT_BLOCKED_EVENT, AccountBlockedDetail } from '../shared/utils/accountStatus';

const CONTACT_EMAIL = 'info@itobox.com';
const PRICING_URL = 'https://www.sistemacourier.com/#pricing';
const DAY = 24 * 60 * 60 * 1000;
const MANAGERS = ['admin', 'super_admin'];

const AccountStatusNotice: React.FC = () => {
    const { tenant } = useTenant();
    const { userData } = useAuth();
    const [blocked, setBlocked] = useState<AccountBlockedDetail | null>(null);

    useEffect(() => {
        const onBlocked = (e: Event) => setBlocked((e as CustomEvent<AccountBlockedDetail>).detail);
        window.addEventListener(ACCOUNT_BLOCKED_EVENT, onBlocked);
        return () => window.removeEventListener(ACCOUNT_BLOCKED_EVENT, onBlocked);
    }, []);

    if (!tenant) return null;
    const isManager = MANAGERS.includes(userData?.role || '');

    // Empresa de demostración (demo.sistemacourier.com): aviso fijo
    if (tenant.subdomain === 'demo') {
        return (
            <div className="mb-4 rounded-xl border border-[#F6C27A] bg-[#FFF8EC] px-4 py-3 text-sm text-[#7A4B0B]">
                <b>Cuenta de demostración.</b> Los datos son de ejemplo y se reinician cada cierto tiempo.
                La marca, los métodos de pago, los usuarios y el envío de correos están desactivados.
            </div>
        );
    }

    // Bloqueo: lo avisa el servidor (403) o se deduce del estado de la empresa
    const trialEnd = tenant.trial_ends_at ? new Date(tenant.trial_ends_at).getTime() : null;
    const trialExpired = tenant.status === 'trial' && trialEnd !== null && trialEnd < Date.now();
    const lock = blocked
        ?? (trialExpired ? { error: 'Prueba expirada' }
            : tenant.status === 'suspended' ? { error: 'Cuenta suspendida' }
            : tenant.status === 'cancelled' ? { error: 'Cuenta cancelada' }
            : null);

    if (lock) {
        const title = lock.error === 'Prueba expirada' ? 'Tu prueba gratis terminó' : lock.error;
        return (
            <div role="alertdialog" aria-modal="true" aria-labelledby="account-lock-title"
                className="fixed inset-0 z-[100] bg-gray-900/70 flex items-center justify-center p-4">
                <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 text-center">
                    <Lock className="mx-auto text-gray-700" size={40} />
                    <h2 id="account-lock-title" className="text-xl font-bold text-gray-900 mt-3">{title}</h2>
                    {isManager ? (
                        <>
                            <p className="text-gray-600 mt-2">
                                Tus datos están guardados. Para seguir usando el sistema, activa un plan:
                                escríbenos a <a className="text-blue-600 underline" href={`mailto:${CONTACT_EMAIL}?subject=Activar plan - ${tenant.subdomain}`}>{CONTACT_EMAIL}</a> y
                                te indicamos cómo pagar por SINPE o transferencia.
                            </p>
                            <a href={PRICING_URL} className="inline-block mt-4 px-4 py-2 rounded-lg bg-blue-600 text-white text-sm hover:bg-blue-700">
                                Ver planes y precios
                            </a>
                        </>
                    ) : (
                        <p className="text-gray-600 mt-2">
                            El servicio de {tenant.branding?.company_name || tenant.name} no está disponible en este momento.
                            Comunícate con la empresa para más información.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    // Franja durante la prueba gratis (solo para quien puede activar el plan)
    if (tenant.status === 'trial' && trialEnd !== null && isManager) {
        const daysLeft = Math.max(0, Math.ceil((trialEnd - Date.now()) / DAY));
        return (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex flex-wrap items-center gap-2">
                <Clock size={16} className="shrink-0" />
                <span>
                    Prueba gratis: {daysLeft === 0 ? 'termina hoy' : `te ${daysLeft === 1 ? 'queda 1 día' : `quedan ${daysLeft} días`}`}.
                    Para activar tu plan escríbenos a{' '}
                    <a className="underline font-medium" href={`mailto:${CONTACT_EMAIL}?subject=Activar plan - ${tenant.subdomain}`}>{CONTACT_EMAIL}</a>.
                </span>
            </div>
        );
    }

    return null;
};

export default AccountStatusNotice;
